import React, { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import api from "@/lib/api";
import { MapPin, Clock, Warning, User as UserIcon, ChartBar, CaretDown } from "@phosphor-icons/react";

const fmtDur = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export default function GpsHeatmapPanel() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);

  useEffect(() => {
    api.get("/mapbox/config").then((r) => setToken(r.data?.public_token || null)).catch(() => {});
  }, []);

  // Load drivers for the date
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/gps/drivers?date=${dateStr}`);
        setDrivers(r.data.drivers || []);
        if (r.data.drivers?.length) setDriverId((cur) => cur || r.data.drivers[0].driver_id);
      } catch (_e) { setDrivers([]); }
    })();
  }, [dateStr]);

  // Load heatmap
  const load = useCallback(async () => {
    if (!dateStr) return;
    setLoading(true);
    try {
      const url = driverId ? `/gps/heatmap?date=${dateStr}&driver_id=${driverId}` : `/gps/heatmap?date=${dateStr}`;
      const r = await api.get(url);
      setData(r.data);
    } catch (_e) { setData(null); }
    finally { setLoading(false); }
  }, [dateStr, driverId]);
  useEffect(() => { load(); }, [load]);

  // Init map
  useEffect(() => {
    if (!token || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [10.45, 51.16],
      zoom: 5,
      cooperativeGestures: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;
  }, [token]);

  // Render tracks + stops
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;

    const draw = () => {
      // Remove previous
      ["gps-tracks", "gps-stops"].forEach((id) => {
        if (map.getLayer(id + "-heat")) map.removeLayer(id + "-heat");
        if (map.getLayer(id + "-point")) map.removeLayer(id + "-point");
        if (map.getLayer(id + "-label")) map.removeLayer(id + "-label");
        if (map.getSource(id)) map.removeSource(id);
      });

      const tracks = data.tracks || [];
      const stops = (data.stops || []).filter((s) => s.lat && s.lng);

      // Track heatmap
      if (tracks.length) {
        map.addSource("gps-tracks", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: tracks.map((t) => ({ type: "Feature", geometry: { type: "Point", coordinates: [t.lng, t.lat] }, properties: {} })),
          },
        });
        map.addLayer({
          id: "gps-tracks-heat",
          type: "heatmap",
          source: "gps-tracks",
          paint: {
            "heatmap-weight": 0.4,
            "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 8, 0.8, 15, 3],
            "heatmap-color": [
              "interpolate", ["linear"], ["heatmap-density"],
              0, "rgba(33, 102, 172, 0)",
              0.2, "rgba(103, 169, 207, 0.5)",
              0.4, "rgba(209, 229, 240, 0.7)",
              0.6, "rgba(253, 219, 199, 0.85)",
              0.8, "rgba(239, 138, 98, 0.9)",
              1, "rgba(178, 24, 43, 1)"
            ],
            "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 8, 15, 15, 40],
            "heatmap-opacity": 0.7,
          },
        });
      }

      // Stops as circles
      if (stops.length) {
        map.addSource("gps-stops", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: stops.map((s, i) => ({
              type: "Feature",
              geometry: { type: "Point", coordinates: [s.lng, s.lat] },
              properties: {
                idx: i + 1,
                name: s.customer_name,
                outlier: s.is_outlier ? 1 : 0,
                reason: s.outlier_reason || "",
                dwell: fmtDur(s.dwell_seconds),
                start: fmtTime(s.start_at),
                done: fmtTime(s.done_at),
              },
            })),
          },
        });
        map.addLayer({
          id: "gps-stops-point",
          type: "circle",
          source: "gps-stops",
          paint: {
            "circle-radius": ["case", ["==", ["get", "outlier"], 1], 12, 8],
            "circle-color": ["case", ["==", ["get", "outlier"], 1], "#dc2626", "#059669"],
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 2,
          },
        });
        map.addLayer({
          id: "gps-stops-label",
          type: "symbol",
          source: "gps-stops",
          layout: {
            "text-field": ["to-string", ["get", "idx"]],
            "text-size": 11,
            "text-allow-overlap": true,
          },
          paint: { "text-color": "#ffffff" },
        });

        map.on("click", "gps-stops-point", (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const p = f.properties;
          new mapboxgl.Popup({ offset: 12 })
            .setLngLat(f.geometry.coordinates)
            .setHTML(`<div style="font-family:system-ui;font-size:12px;padding:2px 4px;min-width:180px">
              <b>#${p.idx} ${p.name}</b><br/>
              Dauer: <b>${p.dwell}</b>${p.outlier === "1" ? ` <span style="color:#dc2626;font-weight:bold">⚠ ${p.reason === "zu_lang" ? "zu lang" : "zu kurz"}</span>` : ""}<br/>
              Start: ${p.start} · Ende: ${p.done}
            </div>`)
            .addTo(map);
        });
        map.on("mouseenter", "gps-stops-point", () => (map.getCanvas().style.cursor = "pointer"));
        map.on("mouseleave", "gps-stops-point", () => (map.getCanvas().style.cursor = ""));

        // Fit bounds
        const bounds = new mapboxgl.LngLatBounds();
        stops.forEach((s) => bounds.extend([s.lng, s.lat]));
        tracks.forEach((t) => bounds.extend([t.lng, t.lat]));
        if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 500 });
      } else if (tracks.length) {
        const bounds = new mapboxgl.LngLatBounds();
        tracks.forEach((t) => bounds.extend([t.lng, t.lat]));
        if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 500 });
      }
    };

    if (map.isStyleLoaded()) draw();
    else map.once("load", draw);
  }, [data]);

  const summary = data?.summary || {};
  const stops = data?.stops || [];
  const outliers = stops.filter((s) => s.is_outlier);

  return (
    <div className="space-y-5" data-testid="gps-heatmap-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <MapPin size={22} weight="bold" /> GPS-Analyse
          </h2>
          <p className="text-xs text-zinc-500 mt-1">Fahrer-Tracks + Stopp-Dwell-Zeiten. Ausreißer (rot) markieren zu lange (&gt; 30 min) oder zu kurze (&lt; 30 s) Stopps.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            className="h-9 px-3 rounded-md border border-black/10 text-sm"
            data-testid="gps-date-input"
          />
          <select
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            className="h-9 px-3 rounded-md border border-black/10 text-sm min-w-[180px]"
            data-testid="gps-driver-select"
          >
            <option value="">Alle Fahrer</option>
            {drivers.map((d) => (
              <option key={d.driver_id} value={d.driver_id}>{d.name} ({d.points} Pts)</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Stopps" value={summary.total_stops || 0} icon={<MapPin size={14} weight="bold" />} testid="kpi-stops" />
        <Kpi label="Track-Punkte" value={summary.total_track_points || 0} icon={<ChartBar size={14} weight="bold" />} testid="kpi-tracks" />
        <Kpi label="Ø Dauer/Stopp" value={fmtDur(summary.avg_dwell_seconds)} icon={<Clock size={14} weight="bold" />} testid="kpi-avg" />
        <Kpi label="Ausreißer" value={summary.outlier_count || 0} icon={<Warning size={14} weight="bold" />} tone={summary.outlier_count > 0 ? "red" : "zinc"} testid="kpi-outliers" />
      </div>

      {/* Map */}
      <div className="bg-white border border-black/10 rounded-lg p-3">
        <div ref={containerRef} className="w-full rounded-md" style={{ height: 480 }} data-testid="gps-map" />
        {!token && <div className="text-xs text-amber-600 mt-2">Mapbox-Token fehlt — Karte kann nicht gerendert werden.</div>}
        {loading && <div className="text-xs text-zinc-500 mt-2">Lade GPS-Daten…</div>}
        {!loading && data && stops.length === 0 && data.tracks.length === 0 && (
          <div className="text-xs text-zinc-400 mt-2 italic">Keine GPS-Daten für dieses Datum / diesen Fahrer.</div>
        )}
      </div>

      {/* Outliers list */}
      {outliers.length > 0 && (
        <section className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="outliers-section">
          <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2 text-red-800">
            <Warning size={16} weight="bold" /> {outliers.length} auffällige Stopps
          </h3>
          <div className="space-y-2">
            {outliers.map((s, i) => (
              <div key={s.job_id} className="flex items-center justify-between gap-3 p-2.5 bg-white border border-red-100 rounded-md text-xs" data-testid={`outlier-${s.job_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">#{stops.indexOf(s) + 1} {s.customer_name}</div>
                  <div className="text-zinc-500 truncate">{s.customer_street}, {s.customer_zip} {s.customer_city}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-red-700">{fmtDur(s.dwell_seconds)}</div>
                  <div className="text-[10px] text-red-600">
                    {s.outlier_reason === "zu_lang" ? "zu lang" : "zu kurz"} · {fmtTime(s.start_at)} → {fmtTime(s.done_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* All stops list */}
      {stops.length > 0 && (
        <section className="bg-white border border-black/10 rounded-lg p-4" data-testid="stops-section">
          <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
            <UserIcon size={16} weight="bold" /> Alle Stopps ({stops.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-black/10 text-left text-zinc-500">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 pr-2">Kunde</th>
                  <th className="py-2 pr-2">Start</th>
                  <th className="py-2 pr-2">Ende</th>
                  <th className="py-2 pr-2 text-right">Dauer</th>
                  <th className="py-2 pr-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {stops.map((s, i) => (
                  <tr key={s.job_id} className={`border-b border-black/5 ${s.is_outlier ? "bg-red-50" : ""}`} data-testid={`stop-row-${s.job_id}`}>
                    <td className="py-1.5 pr-2 font-mono">{i + 1}</td>
                    <td className="py-1.5 pr-2 font-semibold">{s.customer_name}</td>
                    <td className="py-1.5 pr-2 font-mono">{fmtTime(s.start_at)}</td>
                    <td className="py-1.5 pr-2 font-mono">{fmtTime(s.done_at)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono font-bold">{fmtDur(s.dwell_seconds)}</td>
                    <td className="py-1.5 pr-2">
                      {s.is_outlier ? (
                        <span className="text-red-700 font-bold">⚠ {s.outlier_reason === "zu_lang" ? "zu lang" : "zu kurz"}</span>
                      ) : (
                        <span className="text-emerald-700">ok</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Kpi({ label, value, icon, tone, testid }) {
  const cls = tone === "red" ? "text-red-700 bg-red-50 border-red-200" : "text-zinc-900 bg-white border-black/10";
  return (
    <div className={`p-3 border rounded-lg ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{icon}{label}</div>
      <div className="text-xl font-bold font-heading">{value}</div>
    </div>
  );
}
